import _ from 'lodash';

import {
  ActivityType,
  ActivityUseType,
  MailSequenceEnrollmentActivityDataFragment,
  MailSequenceActivityInput,
  MailSequenceSnapshotActivityDataFragment,
  SignatureUser,
} from '../../../generated/graphql';
import {
  ActivityOrResourceOptions,
  ContentType,
  FollowUpDecoratorKeys,
  SavedActivityInputPreview,
} from '../../lib/followUp';
import { SequenceActivityRow } from '../PatientProfile/Sequences/SequenceActivitiesList';

export const toEnrollmentActivityInputs = (
  activities: SavedActivityInputPreview[],
): MailSequenceActivityInput[] =>
  activities.map((activity) => {
    const raw = _.omit(
      activity.sendFollowUpActivityInputRaw,
      FollowUpDecoratorKeys,
    ) as Record<string, unknown>;
    const {
      activityPDFId,
      activityImageId,
      activityLinkId,
      activityAudioId,
      activityTextId,
      assessmentId,
      activityType,
      providerNote,
      isCompletable,
      programAssessmentSignatureUsers,
    } = raw;
    return {
      ...(providerNote ? { providerNote: String(providerNote) } : {}),
      ...(isCompletable !== undefined
        ? { isCompletable: Boolean(isCompletable) }
        : {}),
      ...(programAssessmentSignatureUsers
        ? {
            programAssessmentSignatureUsers:
              programAssessmentSignatureUsers as MailSequenceActivityInput['programAssessmentSignatureUsers'],
          }
        : {}),
      activityType: String(activityType),
      ...(assessmentId ? { assessmentId: String(assessmentId) } : {}),
      ...(activityAudioId ? { activityAudioId: String(activityAudioId) } : {}),
      ...(activityImageId ? { activityImageId: String(activityImageId) } : {}),
      ...(activityLinkId ? { activityLinkId: String(activityLinkId) } : {}),
      ...(activityPDFId ? { activityPdfId: String(activityPDFId) } : {}),
      ...(activityTextId ? { activityTextId: String(activityTextId) } : {}),
    } as MailSequenceActivityInput;
  });

export const hasProviderFirstSigner = (
  activities: SavedActivityInputPreview[],
): boolean =>
  activities
    .filter((a) => a.multiSignatureAssessmentConfig)
    .some((activity) => {
      const firstSigner =
        activity.multiSignatureAssessmentConfig?.assessmentSignatureUsers?.[0];
      return (
        firstSigner &&
        (firstSigner.signatureUserType === SignatureUser.SendingProviderUser ||
          firstSigner.signatureUserType === SignatureUser.AssignedProviderUser)
      );
    });

export const hasAssignedTeamMemberSigner = (
  activities: SavedActivityInputPreview[],
): boolean =>
  activities.some((activity) =>
    activity.multiSignatureAssessmentConfig?.assessmentSignatureUsers?.some(
      (signer) =>
        signer.signatureUserType === SignatureUser.AssignedProviderUser,
    ),
  );

const CONTENT_TYPE_LABEL: Partial<Record<ContentType, string>> = {
  [ContentType.Action]: 'Action',
  [ContentType.Assessment]: 'Assessment',
  [ContentType.Form]: 'Custom form',
  [ContentType.Audio]: 'Audio',
  [ContentType.Image]: 'Image',
  [ContentType.Link]: 'Link',
  [ContentType.PDF]: 'PDF',
  [ContentType.PdfForm]: 'PDF form',
  [ContentType.Esign]: 'E-sign document',
  [ContentType.Text]: 'Text',
};

export const toComposerActivityRows = (
  activities: SavedActivityInputPreview[],
): SequenceActivityRow[] =>
  activities.map((activity) => {
    const cardProps = activity.activityPreviewCardProps;
    const subtitle =
      CONTENT_TYPE_LABEL[cardProps.contentType] ?? cardProps.contentType;
    const signerCount =
      activity.multiSignatureAssessmentConfig?.assessmentSignatureUsers
        ?.length ?? 0;
    return {
      title: cardProps.title || subtitle || 'Activity',
      subtitle,
      providerNote: cardProps.providerNote ?? null,
      signerCount,
      isAvailable: true,
      unavailableReason: null,
    };
  });

const ACTIVITY_TYPE_TO_CONTENT: Partial<Record<string, ContentType>> = {
  Assessment: ContentType.Assessment,
  Action: ContentType.Action,
  Link: ContentType.Link,
  PDF: ContentType.PDF,
  Audio: ContentType.Audio,
  Image: ContentType.Image,
  Text: ContentType.Text,
};

export const snapshotActivityToSavedPreview = (
  input: MailSequenceSnapshotActivityDataFragment,
  hydrated?: MailSequenceEnrollmentActivityDataFragment,
): SavedActivityInputPreview => {
  const activityType =
    (input.activityType as ActivityType) || ActivityType.Action;
  const contentType =
    ACTIVITY_TYPE_TO_CONTENT[input.activityType] ?? ContentType.Action;

  const raw: SavedActivityInputPreview['sendFollowUpActivityInputRaw'] = {
    isSubmittable: true,
    isCompletable: input.isCompletable ?? true,
    activityType,
    providerNote: input.providerNote ?? '',
    assessmentId: input.assessmentId ?? undefined,
    activityPDFId: input.activityPdfId ?? undefined,
    activityLinkId: input.activityLinkId ?? undefined,
    activityAudioId: input.activityAudioId ?? undefined,
    activityImageId: input.activityImageId ?? undefined,
    activityTextId: input.activityTextId ?? undefined,
    dateCreated: Date.now(),
  };

  return {
    sendFollowUpActivityInputRaw: raw,
    activityPreviewCardProps: {
      contentType,
      activityUseType: ActivityUseType.OneTimeUse,
      title: hydrated?.title ?? input.activityType,
      activityOrResourceOption: ActivityOrResourceOptions.Resource,
      providerNote: hydrated?.providerNote ?? input.providerNote ?? undefined,
    },
  };
};

export const snapshotActivitiesToSavedPreviews = (
  inputs: MailSequenceSnapshotActivityDataFragment[],
  hydrated?: MailSequenceEnrollmentActivityDataFragment[],
): SavedActivityInputPreview[] =>
  inputs.map((input, i) =>
    snapshotActivityToSavedPreview(input, hydrated?.[i]),
  );
